import {
	IconBook2,
	IconCalendar,
	IconCalendarEvent,
	IconDownload,
	IconUsersGroup,
	IconVideo,
} from "@tabler/icons-react"
import { type FC, useEffect, useMemo, useState } from "react"

import type { LandingPageProductHighlightBlock, ProductHighlightProductType } from "@forento/core/models/landing"
import { landingPageCopyCharacterLimits, productHighlightProductTypes } from "@forento/core/schemas/landing"

import { query } from "~/utilities/trpc"

import EditableField from "./EditableField"
import EditorShell from "./EditorShell"
import ProductLinkTargetField from "./ProductLinkTargetField"

const productTypeLabels: Record<ProductHighlightProductType, string> = {
	course: "Course",
	downloadable: "Downloadable",
	video: "Video",
	session: "Session",
	event: "Event",
	membership: "Membership",
}

const productTypeIcons: Record<ProductHighlightProductType, FC<{ className?: string }>> = {
	course: IconBook2,
	downloadable: IconDownload,
	video: IconVideo,
	session: IconCalendarEvent,
	event: IconCalendar,
	membership: IconUsersGroup,
}

type Props = {
	block: LandingPageProductHighlightBlock
	onChange: (block: LandingPageProductHighlightBlock) => void
	onClose: () => void
}
const ProductHighlightBlockEditor: FC<Props> = ({ block, onChange, onClose }) => {
	const { data: options = [], isLoading } = query.landing.productHighlightOptions.useQuery(undefined, {
		refetchOnWindowFocus: false,
	})
	const [title, setTitle] = useState(block.content.title ?? "")
	const [productType, setProductType] = useState<ProductHighlightProductType>(block.content.productType)
	const [productId, setProductId] = useState<number | null>(block.content.productId)
	const [linkTarget, setLinkTarget] = useState(block.content.linkTarget ?? "product-page")

	useEffect(() => {
		onChange({
			type: "product-highlight",
			content: { title: title.trim() || undefined, productType, productId, linkTarget },
		})
	}, [linkTarget, onChange, productId, productType, title])

	const filteredOptions = useMemo(() => options.filter(option => option.type === productType), [options, productType])
	const selectedProduct = filteredOptions.find(option => option.id === productId) ?? null
	const linksToCheckout = linkTarget === "checkout" && selectedProduct?.hasPrice === true
	const PlaceholderIcon = productTypeIcons[productType]

	return (
		<EditorShell onClose={onClose}>
			<section className="px-6 pt-16 pb-8 sm:pt-20 lg:px-8">
				<div className="mx-auto max-w-7xl">
					<EditableField
						value={title}
						onChange={setTitle}
						placeholder="Product highlight title"
						className="mb-12 text-center"
						maxCharacters={landingPageCopyCharacterLimits.title}
						inputClassName="text-center font-bold text-3xl text-gray-900 tracking-tight sm:text-4xl"
					/>
					<ProductLinkTargetField value={linkTarget} onChange={setLinkTarget} />
					<div className="mb-6 grid gap-4 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-5 sm:grid-cols-2">
						<label className="grid gap-2">
							<span className="text-sm font-semibold text-gray-700">Product type</span>
							<select
								value={productType}
								onChange={event => {
									const nextType = event.target.value as ProductHighlightProductType
									const nextOptions = options.filter(option => option.type === nextType)
									setProductType(nextType)
									setProductId(nextOptions[0]?.id ?? null)
								}}
								className="cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm focus:ring-2 focus:ring-indigo-200 focus:outline-none"
							>
								{productHighlightProductTypes.map(type => (
									<option key={type} value={type}>
										{productTypeLabels[type]}
									</option>
								))}
							</select>
						</label>
						<label className="grid gap-2">
							<span className="text-sm font-semibold text-gray-700">Product</span>
							<select
								value={productId ?? ""}
								onChange={event => setProductId(event.target.value ? Number(event.target.value) : null)}
								className="cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm focus:ring-2 focus:ring-indigo-200 focus:outline-none"
							>
								<option value="">{isLoading ? "Loading products..." : "Select a product"}</option>
								{filteredOptions.map(option => (
									<option key={option.id} value={option.id}>
										{option.label}
									</option>
								))}
							</select>
						</label>
					</div>
					<div className="grid overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.35fr)]">
						<div className="flex min-h-80 items-center justify-center bg-linear-to-br from-gray-200 to-gray-300">
							<PlaceholderIcon className="size-16 text-gray-400" />
						</div>
						<div className="flex flex-col justify-center p-8 sm:p-10 lg:p-12">
							<h3 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
								{selectedProduct?.label ?? "Select a product"}
							</h3>
							<p className="mt-6 text-base leading-8 text-gray-600">
								The product short description, image, price, and link will be loaded from the selected
								product on the published landing page.
							</p>
							<div className="mt-5 flex flex-wrap items-center gap-3">
								<span className="landing-button inline-flex cursor-pointer items-center px-5 py-3 text-sm font-semibold">
									{linksToCheckout ? "Go to checkout" : "View product"}
								</span>
								<span className="font-semibold text-gray-900">Price</span>
							</div>
						</div>
					</div>
				</div>
			</section>
		</EditorShell>
	)
}

export default ProductHighlightBlockEditor
